import { useCallback, useState } from 'react';
import { useLogin } from '../../../../hooks/useLogin';
import {
  isLoginResultSuccess,
  getLoginErrorCode,
  loginByEmail,
  LoginResult,
  loginByAuthorizationCode,
  loginByLegacyApiToken,
  loginByUUID,
  deleteTokens,
  HttpErrorCode,
} from '../../../../api/jwtAuth';
import { ApiTokenType } from '../../../../context/AuthContext';

export const useJwtAuth = () => {
  const [loginErrorCode, setLoginErrorCode] = useState<
    HttpErrorCode | undefined
  >();
  const login = useLogin();

  const processLoginResult = useCallback(
    (loginResult: LoginResult): 'success' | LoginResult => {
      if (isLoginResultSuccess(loginResult)) {
        login(`Bearer ${loginResult.accessToken}`, ApiTokenType.JWT);
        return 'success';
      } else {
        setLoginErrorCode(
          getLoginErrorCode(loginResult) ?? 'INVALID_CREDENTIALS',
        );
        return loginResult;
      }
    },
    [login],
  );

  const doLogin = useCallback(
    async (email: string, password: string) => {
      const loginResult = await loginByEmail(email, password);
      processLoginResult(loginResult);
    },
    [processLoginResult],
  );

  const doLoginByAuthorizationCode = useCallback(
    async (authorizationCode: string) => {
      await deleteTokens();
      const loginResult = await loginByAuthorizationCode(authorizationCode);
      processLoginResult(loginResult);
    },
    [processLoginResult],
  );

  const doLoginByLegacyApiToken = useCallback(
    async (apiToken: string) => {
      await deleteTokens();
      const loginResult = await loginByLegacyApiToken(apiToken);
      processLoginResult(loginResult);
    },
    [processLoginResult],
  );

  const doLoginByUUID = useCallback(
    async (
      uuid: string,
      organizationId: string,
    ): Promise<'success' | LoginResult> => {
      const loginResult = await loginByUUID(uuid, organizationId);
      return processLoginResult(loginResult);
    },
    [processLoginResult],
  );

  const loginErrors = loginErrorCode !== undefined;

  return {
    doLogin,
    doLoginByAuthorizationCode,
    doLoginByLegacyApiToken,
    doLoginByUUID,
    loginErrors,
    loginErrorCode,
  };
};
